import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { axiosServiceInstance } from '@onesource/services';
import { FC, useEffect, useState } from 'react';

export interface UpdatePasswordFormProps {
  /** URL to redirect to after successful password update (default: '/auth/login') */
  redirectTo?: string;
  /** Minimum password length (default: 8) */
  minPasswordLength?: number;
  /** Callback when password is updated successfully */
  onSuccess?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Custom title (default: "Set New Password") */
  title?: string;
  /** Custom styles for the form container */
  sx?: SxProps<Theme>;
}

export const UpdatePasswordForm: FC<UpdatePasswordFormProps> = ({
  redirectTo = '/auth/login',
  minPasswordLength = 8,
  onSuccess,
  onError,
  title = 'Set New Password',
  sx,
}) => {
  const theme = useTheme();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  // Extract token from URL query params or hash
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Invalid or expired password reset link. Please request a new one.');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < minPasswordLength) {
      setError(`Password must be at least ${minPasswordLength} characters long`);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Missing reset token');
      return;
    }

    setIsLoading(true);

    try {
      await axiosServiceInstance.post('/auth/set-password', { token, password });
      setSuccess(true);
      onSuccess?.();
      setTimeout(() => {
        window.location.href = redirectTo;
      }, 3000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update password';
      setError(errorMessage);
      onError?.(new Error(errorMessage));
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: 'success.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            <Box
              component="svg"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              sx={{ width: 36, height: 36, color: 'success.main' }}
            >
              <polyline points="20 6 9 17 4 12" />
            </Box>
          </Box>
          <Typography variant="h5" fontWeight={700}>
            Password Updated Successfully
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Redirecting to login...
          </Typography>
          <LinearProgress sx={{ width: '60%', mt: 1, borderRadius: 1 }} />
        </Box>
      </Container>
    );
  }

  // Invalid token state
  if (!token && error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 3,
          }}
        >
          <Typography variant="h5" color="error">
            Invalid Reset Link
          </Typography>
          <Typography color="text.secondary" textAlign="center">
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => {
              window.location.href = redirectTo;
            }}
          >
            Back to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            p: 4,
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            boxShadow: 3,
            ...sx,
          }}
        >
          <Typography variant="h5" textAlign="center" mb={3}>
            {title}
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              margin="normal"
              required
              autoFocus
              helperText={`Minimum ${minPasswordLength} characters`}
            />

            <TextField
              fullWidth
              type="password"
              label="Confirm Password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              margin="normal"
              required
            />

            {error && (
              <Typography color="error" sx={{ mt: 2 }} variant="body2">
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading || !password || !confirmPassword}
              sx={{ mt: 3 }}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Update Password'}
            </Button>
          </form>
        </Box>
      </Box>
    </Container>
  );
};
